import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import { cn } from "@/lib/utils";
import { EASE } from "@/components/motion";

export function Logo({ light = false }) {
  return (
    <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
      <span className="grid h-10 w-10 place-items-center bg-gold font-heading text-lg font-extrabold text-navy">
        F
      </span>
      <span className="flex flex-col leading-none">
        <span className={cn("font-heading text-lg font-extrabold tracking-tight", light ? "text-white" : "text-navy")}>
          FORMCORE
        </span>
        <span className={cn("text-[10px] font-semibold uppercase tracking-[0.45em]", light ? "text-white/60" : "text-charcoal/60")}>
          Constructions
        </span>
      </span>
    </Link>
  );
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur-xl transition-shadow duration-300",
          scrolled ? "border-navy/10 shadow-[0_1px_24px_rgba(11,31,51,0.08)]" : "border-transparent"
        )}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
          <Logo />
          <nav className="hidden items-center gap-7 xl:flex" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  cn(
                    "relative text-[13px] font-semibold uppercase tracking-[0.12em] transition-colors duration-200",
                    isActive ? "text-gold" : "text-charcoal/70 hover:text-navy"
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="header-request-workforce-btn"
              className="hidden items-center gap-2 bg-gold px-5 py-3 font-heading text-[13px] font-bold uppercase tracking-[0.12em] text-navy transition-colors duration-200 hover:bg-gold-dark active:scale-[0.97] sm:inline-flex"
            >
              Request Workforce
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-btn"
              className="grid h-11 w-11 place-items-center border border-navy/15 text-navy xl:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] flex flex-col bg-navy"
            data-testid="mobile-menu"
          >
            <div className="flex h-20 items-center justify-between px-6">
              <Logo light />
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close-btn"
                className="grid h-11 w-11 place-items-center border border-white/20 text-white"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: EASE }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      cn(
                        "font-heading text-3xl font-extrabold tracking-tight transition-colors sm:text-4xl",
                        isActive ? "text-gold" : "text-white hover:text-gold"
                      )
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5, ease: EASE }}
                className="mt-10"
              >
                <Link
                  to="/contact"
                  data-testid="mobile-request-workforce-btn"
                  className="inline-flex items-center gap-2 bg-gold px-7 py-4 font-heading text-sm font-bold uppercase tracking-[0.12em] text-navy"
                >
                  Request Workforce
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
